/**
 * The Carbon app frame (blueprint §4.6): a full-height column of the 48px
 * header (in normal flow), then a row of the side nav and <main>. <main> is
 * the only scroller: the mockups' window scroll with a fixed header is
 * deliberately not copied, so route-change scroll resets, sticky bars at
 * top-0 and IntersectionObserver roots keep working.
 *
 * Below 1056px the side nav is a drawer over a 60% black scrim (data-shell,
 * so the light theme never reaches it). Opening it moves focus to the first
 * control inside it; the scrim and Escape close it and hand focus back to the
 * menu button. The caller makes <main> inert while it is open (inertWhen). A
 * resize past 1056px closes it, so the in-flow nav never leaves <main> inert.
 * The skip link leads to the element with id `mainId`.
 */
import { useCallback, useEffect } from 'react'
import clsx from 'clsx'
import { controllerOf, firstFocusable } from './a11y'
import type { AppShellProps } from './types'

/** Carbon's lg breakpoint (the `c-lg` screen): the nav is in flow from here up. */
const IN_FLOW = '(min-width: 1056px)'

export function AppShell({ header, nav, navOpen, onCloseNav, navId = 'side-nav', mainId = 'main', children }: AppShellProps) {
  const closeAndReturn = useCallback(() => {
    onCloseNav()
    controllerOf(navId)?.focus()
  }, [onCloseNav, navId])

  useEffect(() => {
    if (!navOpen) return
    // Focus the drawer's first control. Its visibility rides the slide transition, so the first frame can still be
    // `visibility: hidden` (focus() is then a no-op); try again once the 240ms slide has run.
    const nav = document.getElementById(navId)
    const focusIn = () => {
      if (!nav?.contains(document.activeElement)) firstFocusable(nav)?.focus()
    }
    const frame = requestAnimationFrame(focusIn)
    const settle = window.setTimeout(focusIn, 260)
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') closeAndReturn()
    }
    const wide = window.matchMedia(IN_FLOW)
    const onWide = () => {
      if (wide.matches) onCloseNav()
    }
    document.addEventListener('keydown', onKey)
    wide.addEventListener('change', onWide)
    return () => {
      cancelAnimationFrame(frame)
      window.clearTimeout(settle)
      document.removeEventListener('keydown', onKey)
      wide.removeEventListener('change', onWide)
    }
  }, [navOpen, navId, closeAndReturn, onCloseNav])

  return (
    <div className="flex h-screen flex-col">
      <a
        href={`#${mainId}`}
        className="sr-only focus:not-sr-only focus:fixed focus:left-0 focus:top-0 focus:z-[100] focus:bg-interactive focus:px-4 focus:py-3.5 focus:text-interactive-on"
      >
        Skip to main content
      </a>
      {header}
      <div className="relative flex min-h-0 flex-1">
        {nav}
        <div
          data-shell=""
          aria-hidden="true"
          onClick={closeAndReturn}
          className={clsx(
            'fixed inset-x-0 bottom-0 top-12 z-[25] bg-black/60 transition-opacity duration-150 ease-carbon c-lg:hidden',
            navOpen ? 'opacity-100' : 'pointer-events-none opacity-0',
          )}
        />
        {children}
      </div>
    </div>
  )
}

export default AppShell
